import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useJoinTournament } from '../hooks/useJoinTournament';
import GasBanner from '../components/GasBanner';
import './FortniteLobby.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const REFRESH_FILLING = 5_000;
const REFRESH_ACTIVE  = 30_000;
const MAX_PLAYERS     = 100;

function formatUSDC(raw) {
  if (!raw) return '$0';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function useCountdown(windowEnd) {
  const [label, setLabel]     = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!windowEnd) return;
    function tick() {
      const diff = windowEnd * 1000 - Date.now();
      if (diff <= 0) { setLabel('Window closed'); setExpired(true); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setLabel(`${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`);
      setExpired(false);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [windowEnd]);

  return { label, expired };
}

function FortniteCard({ tournament, walletAddress }) {
  const [mode, setMode]               = useState('idle');
  const [epicId, setEpicId]           = useState('');
  const [formError, setFormError]     = useState('');
  const [successData, setSuccessData] = useState(null);
  const { label: timerLabel, expired } = useCountdown(tournament.window_end);
  const { joinTournament, state: joinState, reset: resetJoin } = useJoinTournament();

  const pct = Math.min((tournament.player_count / MAX_PLAYERS) * 100, 100);

  const stepLabel = {
    approving:     'Step 1/3: Approving USDC...',
    joining:       'Step 2/3: Joining on-chain...',
    fetching_stats:'Step 3/3: Fetching your current stats...',
    recording:     'Step 3/3: Fetching your current stats...',
  }[joinState.step];

  async function handleJoin(e) {
    e.preventDefault();
    if (!epicId.trim()) { setFormError('Fortnite username is required'); return; }
    if (/^[0-9a-f]{32}$/i.test(epicId.trim())) {
      setFormError('That looks like an account ID, not your username. Enter your Fortnite in-game name (e.g. Ninja, Tfue).');
      return;
    }
    setFormError('');
    setMode('tx');
    try {
      const data = await joinTournament({
        tournament,
        gamePlayerId: epicId.trim(),
        gameField:    'epicPlayerId',
      });
      setSuccessData(data);
      setMode('success');
    } catch (err) {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to join. Try again.');
      setMode('form');
    }
  }

  return (
    <div className="fn-card">
      <div className="fn-card__game-badge">⚡ FORTNITE</div>

      <div>
        <div className="fn-card__prize-label">Winner Takes (90%)</div>
        <div className="fn-card__prize">{formatUSDC(Math.floor(Number(tournament.prize_pool) * 0.9))}</div>
        <div className="fn-card__prize-pool-sub">Total pool: {formatUSDC(tournament.prize_pool)} · 10% platform fee</div>
      </div>

      <div className="fn-card__meta">
        <span className="fn-card__entry">Entry: {formatUSDC(tournament.entry_fee)}</span>
        {tournament.status === 'active' && (
          <span className={`fn-card__timer${expired ? ' fn-card__timer--expired' : ''}`}>
            {timerLabel}
          </span>
        )}
        {tournament.status === 'filling' && (
          <span className="fn-card__badge-filling">● Filling</span>
        )}
      </div>

      {tournament.status === 'active' && (
        <div className="fn-card__info">
          30 min window · most kills wins · instant USDC payout
        </div>
      )}

      {tournament.status === 'filling' && (
        <div className="fn-card__filling-banner">
          <div className="fn-card__filling-title">⏳ Waiting for full lobby</div>
          <div className="fn-card__filling-sub">
            The 30-minute kill-race starts the moment slot 100 fills. Join now and queue up.
          </div>
        </div>
      )}

      <div>
        <div className="fn-card__progress-row">
          <span>Players</span>
          <span>{tournament.player_count} / {MAX_PLAYERS}</span>
        </div>
        <div className="fn-card__bar-track">
          <div className="fn-card__bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {tournament.players && tournament.players.length > 0 && (
        <div className="fn-card__players">
          <div className="fn-card__players-label">Enrolled players</div>
          <div className="fn-card__players-list">
            {tournament.players.map((p, i) => (
              <div key={i} className="fn-card__player-row">
                <span className="fn-card__player-handle">{p.handle}</span>
                <span className="fn-card__player-wallet">{p.wallet}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'idle' && (
        <button
          className="fn-card__join-btn"
          onClick={() => setMode('form')}
          disabled={tournament.player_count >= MAX_PLAYERS || (tournament.status === 'active' && expired)}
        >
          {tournament.player_count >= MAX_PLAYERS ? 'Full' : (tournament.status === 'active' && expired) ? 'Closed' : 'Join'}
        </button>
      )}

      {(mode === 'form' || mode === 'tx') && (
        <form className="fn-card__form" onSubmit={handleJoin}>
          <div className="fn-card__gas-notice">
            ⛽ Need POL for gas?{' '}
            <a
              href="https://app.uniswap.org/swap?chain=polygon&inputCurrency=0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359&outputCurrency=MATIC&exactAmount=0.10&exactField=input"
              target="_blank"
              rel="noopener noreferrer"
              className="fn-card__gas-link"
            >
              Swap $0.10 USDC → POL →
            </a>
          </div>
          <div className="fn-card__stats-notice">
            ⚠️ Enter your <strong>Fortnite username</strong> (your in-game name, e.g. Ninja, Tfue). Stats must be <strong>Public</strong> in Epic privacy settings or your kills won't count.
          </div>
          <input
            className="fn-card__input"
            placeholder="Fortnite username (e.g. Ninja, Tfue)"
            value={epicId}
            onChange={e => setEpicId(e.target.value)}
            disabled={mode === 'tx'}
            autoFocus
          />
          {formError && <span className="fn-card__form-error">{formError}</span>}
          {mode === 'tx' && stepLabel && (
            <span className="fn-card__form-step">{stepLabel}</span>
          )}
          <div className="fn-card__form-row">
            <button type="button" className="fn-card__cancel-btn"
              onClick={() => { setMode('idle'); setEpicId(''); setFormError(''); resetJoin(); }}
              disabled={mode === 'tx'}>Cancel</button>
            <button type="submit" className="fn-card__submit-btn" disabled={mode === 'tx'}>
              {mode === 'tx' ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </form>
      )}

      {mode === 'success' && successData && (
        <div className="fn-card__success">
          <div className="fn-card__success-title">✅ All good, you're in!</div>
          <div className="fn-card__success-detail">
            {successData.tournament.status === 'active' ? (
              <>Match window is live — launch Fortnite and rack up kills now!</>
            ) : (
              <>Waiting for tournament to start ({successData.tournament.player_count}/{successData.tournament.max_players || 100} players). <strong style={{ color: '#FFDE00' }}>Stay ready</strong> — drop hot the moment the window opens!</>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FortniteLobby({ walletAddress }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref) localStorage.setItem('referralCode', ref);
  }, [location.search]);

  const fetchTournaments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournament/active?game=fortnite`);
      setTournaments(data.tournaments);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load tournaments. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTournaments(); }, [fetchTournaments]);

  useEffect(() => {
    const hasFilling = tournaments.some(t => t.status === 'filling');
    const id = setInterval(fetchTournaments, hasFilling ? REFRESH_FILLING : REFRESH_ACTIVE);
    return () => clearInterval(id);
  }, [tournaments, fetchTournaments]);

  return (
    <div className="fn-lobby">
      <Helmet>
        <title>Fortnite Tournaments — Win USDC for Most Kills | EZGAME</title>
        <meta name="description" content="Join a Fortnite kill tournament for $1 USDC. Most kills in 30 minutes wins 90% of the prize pool, paid instantly on Polygon. Gas under $0.01." />
        <link rel="canonical" href="https://www.ezegames.live/fortnite" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="EZGAME" />
        <meta property="og:locale"      content="en_US" />
        <meta property="og:title"       content="Fortnite Kill Tournaments — Win USDC | EZGAME" />
        <meta property="og:description" content="$1 entry. 30-minute kill window. 90% prize pool to the top Fortnite player — paid instantly in USDC." />
        <meta property="og:url"         content="https://www.ezegames.live/fortnite" />
        <meta property="og:image"       content="https://www.ezegames.live/og-image.png" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@EZ_Egames" />
        <meta name="twitter:title"       content="Fortnite Kill Tournaments — Win USDC | EZGAME" />
        <meta name="twitter:description" content="$1 entry. 30-minute kill window. 90% prize pool paid instantly in USDC on Polygon." />
        <meta name="twitter:image"       content="https://www.ezegames.live/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: 'EZGAME Fortnite Kill Tournament',
          description: 'Fortnite kill tournament — $1 USDC entry, 30-minute match window, 90% of the prize pool to the player with the most eliminations.',
          sport: 'Fortnite',
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
          location: { '@type': 'VirtualLocation', url: 'https://www.ezegames.live/fortnite' },
          organizer: { '@type': 'Organization', name: 'EZGAME', url: 'https://www.ezegames.live' },
          offers: { '@type': 'Offer', price: '1.00', priceCurrency: 'USD', availability: 'https://schema.org/InStock', url: 'https://www.ezegames.live/fortnite' },
          url: 'https://www.ezegames.live/fortnite',
        })}</script>
      </Helmet>
      {/* Hero */}
      <div className="fn-lobby__hero">
        <div className="fn-lobby__hero-bg" />
        <div className="fn-lobby__hero-overlay" />
        <div className="fn-lobby__hero-content">
          <div className="fn-lobby__eyebrow">⚡ Web3 Fortnite Tournaments</div>
          <h1 className="fn-lobby__hero-title">
            Drop. Kill. <span className="fn-gradient-text">Win USDC.</span>
          </h1>
          <p className="fn-lobby__hero-sub">
            Pay $1 USDC, rack up the most kills in 30 minutes, get paid instantly on Polygon.
            Gas under $0.01. Pure skill.
          </p>
        </div>
      </div>

      {/* How it works strip */}
      <div className="fn-lobby__how-strip">
        {[
          { icon: '🎮', text: 'Connect wallet' },
          { icon: '💵', text: 'Pay $1 USDC entry' },
          { icon: '🔫', text: 'Play Fortnite — most kills wins' },
          { icon: '⚡', text: 'Instant payout on Polygon' },
        ].map(s => (
          <div key={s.text} className="fn-lobby__how-item">
            <span className="fn-lobby__how-icon">{s.icon}</span>
            <span className="fn-lobby__how-text">{s.text}</span>
          </div>
        ))}
      </div>

      {/* Tournaments */}
      <div className="fn-lobby__main">
        <GasBanner />
        {/* Stats public warning */}
        <div className="fn-lobby__warning">
          <span className="fn-lobby__warning-icon">⚠️</span>
          <div>
            <strong>Your Epic stats must be set to Public</strong> before joining.
            If your stats are private, we cannot verify your kills and you will be disqualified from the payout.
            {' '}<a
              href="https://www.epicgames.com/account/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="fn-lobby__warning-link"
            >Set stats to public on Epic →</a>
          </div>
        </div>

        <div className="fn-lobby__header">
          <h2 className="fn-lobby__title">Active <span className="fn-gradient-text">Tournaments</span></h2>
          {lastRefresh && (
            <span className="fn-lobby__refresh">Updated {lastRefresh.toLocaleTimeString()}</span>
          )}
        </div>

        {error   && <div className="fn-lobby__error">{error}</div>}
        {loading && <div className="fn-lobby__status">Loading tournaments...</div>}

        {!loading && !error && tournaments.length === 0 && (
          <div className="fn-lobby__status">No active Fortnite tournaments right now. Check back soon!</div>
        )}

        {!loading && tournaments.length > 0 && (
          <div className="fn-lobby__grid">
            {tournaments.map(t => (
              <FortniteCard key={t.id} tournament={t} walletAddress={walletAddress} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
